import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SubCompetencyWithEvaluation } from '../../types/GrowthWheel'
import CompetencyViewCard from '../../components/GrowthWheelComponents/CompetencyViewCard'
import SubCompetencyViewCard from '../../components/GrowthWheelComponents/SubCompetencyViewCard'
import {
  getCompletedCompetencies,
  getListTransitionClass,
  getPageTransitionClass,
  getTotalCompetencies,
} from '../../utils/growth-wheel-utils'
import { useGrowthWheelPagination } from '../../hooks/useGrowthWheelPagination'
import GrowthWheelPagination from '../../components/GrowthWheelPagination/GrowthWheelPagination'
import { useContentTransition } from '../../hooks/useContentTransition'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import {
  setCurrentCompetency,
  setCurrentSubCompetency,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'

interface Props {
  subjectUserId: number
  reviewerUserId?: number
  isAdminView?: boolean
  userName?: string
  guildName?: string
}

const GrowthWheelCompetencyView = ({ userName, guildName }: Props) => {
  const dispatch = useDispatch()
  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const userCompetenciesList = useSelector(
    (state: RootState) => state.growthWheel.userCompetenciesData,
  )

  const handleClickCompetencyChange = (
    competency: Parameters<typeof setCurrentCompetency>[0],
  ) => {
    dispatch(setCurrentCompetency(competency))
  }

  const handleClickSubCompetencyChange = (
    sub: SubCompetencyWithEvaluation | null,
  ) => {
    dispatch(setCurrentSubCompetency(sub))
  }

  if (!currentCompetency) return null
  const subCompetencies = currentCompetency.subCompetencies ?? []
  const totalSubCompetencies = getTotalCompetencies(subCompetencies)
  const completedSubCompetencies = getCompletedCompetencies(subCompetencies)
  const {
    currentIndex: currentCompetencyIndex,
    handlePrevious,
    handleNext,
  } = useGrowthWheelPagination(
    userCompetenciesList,
    currentCompetency,
    handleClickCompetencyChange,
  )
  const { isTransitioning, transitionDirection } =
    useContentTransition(currentCompetencyIndex)

  const getSubCompetencyCardProps = (sub: SubCompetencyWithEvaluation) => {
    const isFocus = sub.evaluation?.focus ?? false
    const showSelfAssessed = Boolean(sub.evaluation?.selfLevel)
    const showGuided = Boolean(sub.evaluation?.leaderLevel)
    const selfLevel = sub.evaluation?.selfLevel ?? null
    const leaderLevel = sub.evaluation?.leaderLevel ?? null
    const status = showSelfAssessed || showGuided

    return {
      isFocus,
      status,
      showSelfAssessed,
      showGuided,
      selfLevel,
      leaderLevel,
    }
  }

  return (
    <div className="container mb-5 gw-competency-area-view">
      {userName ? (
        <div className="gw-competency-area-view-header">
          <BackButton
            onClick={() => handleClickCompetencyChange(null)}
            text="Back to All Competencies"
          />
          <div className="gw-growth-wheel-header-titles">
            <h1 className="gw-growth-wheel-title">
              {`${userName}'s`} Growth Wheel
            </h1>
            {guildName ? (
              <p className="gw-growth-wheel-guild-name">{guildName}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <BackButton
          onClick={() => handleClickCompetencyChange(null)}
          text="Back to All Competencies"
        />
      )}

      <div
        className={`gw-competency-area-view-content gw-competency-area-page ${getPageTransitionClass(
          isTransitioning,
          transitionDirection,
        )}`}
      >
        <CompetencyViewCard
          title={currentCompetency.name}
          description={currentCompetency.description}
          completed={completedSubCompetencies}
          total={totalSubCompetencies}
        />

        <div className="gw-competency-area-competencies">
          <h3 className="gw-competency-area-competencies-title">
            Sub-competencies
          </h3>
          <div
            className={`gw-competency-area-competencies-list ${getListTransitionClass(
              isTransitioning,
              transitionDirection,
            )}`}
          >
            {subCompetencies.map(sub => (
              <SubCompetencyViewCard
                key={sub.id}
                title={sub.name}
                description={sub.description}
                evaluation={getSubCompetencyCardProps(sub)}
                onClick={() => handleClickSubCompetencyChange(sub)}
              />
            ))}
          </div>
        </div>
      </div>

      <GrowthWheelPagination
        currentIndex={currentCompetencyIndex}
        optionsList={userCompetenciesList}
        handleClickPrevious={handlePrevious}
        handleClickNext={handleNext}
        type="Competency"
      />
    </div>
  )
}

export default GrowthWheelCompetencyView
